import { useState, useCallback } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { PagePermissionsUpdateRequest } from "@/interface/PagePermissions";
import { getPagePermissions, updatePagePermissions } from "@/services/pagePermissions";
import { logger } from "@/utils/logger";

export const usePagePermissionsQuery = () => {
  const [isManualReloading, setIsManualReloading] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["pagePermissions"],
    queryFn: getPagePermissions,
    staleTime: 1000 * 60 * 2, // 2 minutes (tighter than global 5m)
    refetchOnMount: true, // Always check on mount
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const refreshPermissions = useCallback(async () => {
    setIsManualReloading(true);
    try {
      await refetch();
    } finally {
      setIsManualReloading(false);
    }
  }, [refetch]);

  return {
    data,
    isLoading,
    isError,
    error: error?.message || null,
    refetch: refreshPermissions,
    isManualReloading,
  };
};

export const useUpdatePagePermissions = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: PagePermissionsUpdateRequest) => updatePagePermissions(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pagePermissions"] });
    },
    onError: (err: any) => {
      logger.error("Failed to update page permissions", {
        error: err,
        context: "useUpdatePagePermissions",
      });
    },
  });

  return {
    updatePermissions: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error?.message || null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
